import React from "react";
import { BaseTableProps, Order } from "./MyGenericTableTypes";
import { useDebounce } from "utils/hooks";
import { MyGenericTable } from "./MyGenericTable";

// There are two prop types here, because I ran into
// a scenario where the table for monthly reports wanted
// to be in control of the filter. I set it up this way
// so tables can opt-in to this setup.
type BasicProps<T extends { id: string }> = {
  data: T[];
} & BaseTableProps<T>;

type FilterProps<T extends { id: string }> = {
  data: T[];
  filterTerm: string;
  setFilterTerm: (value: string) => void;
} & BaseTableProps<T>;

function descendingComparator<T>(
  a: T,
  b: T,
  orderByPropertyName: keyof T,
  reverse: boolean,
) {
  if (b["primaryStatus"] === "Primary") {
    return reverse ? -1 : 1;
  }

  if (a["primaryStatus"] === "Primary") {
    return reverse ? 1 : -1;
  }

  const aValue = a[orderByPropertyName] ?? "";
  const bValue = b[orderByPropertyName] ?? "";

  if (typeof aValue === "string" && typeof bValue === "string") {
    const aString = (aValue as unknown as string).toLocaleLowerCase();
    const bString = (bValue as unknown as string).toLocaleLowerCase();

    if (bString < aString) {
      return -1;
    }
    if (bString > aString) {
      return 1;
    }

    return 0;
  }

  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function getComparator<T>(
  order: Order,
  orderBy: keyof T,
): (a: { [key in keyof T]: any }, b: { [key in keyof T]: any }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy, false)
    : (a, b) => -descendingComparator(a, b, orderBy, true);
}

// Used by components where all data is stored locally
const MyLocalDataTable = <T extends { id: string }>(
  props: BasicProps<T> | FilterProps<T>,
) => {
  const isFilterProps = (
    props: BasicProps<T> | FilterProps<T>,
  ): props is FilterProps<T> => {
    const result = (props as FilterProps<T>).setFilterTerm !== undefined;
    return result;
  };

  const [orderBy, setOrderBy] = React.useState<keyof T>(
    props.defaultSortColumn,
  );

  const [order, setOrder] = React.useState<Order>(
    props.defaultSortOrder ?? "asc",
  );

  const [filterTerm, setFilterTerm] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);

  const debouncedSearchTerm = useDebounce(
    isFilterProps(props) ? props.filterTerm : filterTerm,
    1000,
  );

  const { onFilterChanged, columns, customFilterPredicate, data } = props;

  React.useEffect(() => {
    onFilterChanged && onFilterChanged(debouncedSearchTerm);
  }, [debouncedSearchTerm, onFilterChanged]);

  function stableSort<S>(array: S[], comparator: (a: S, b: S) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [S, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const { filteredSortedAndPaged, smartPage, totalRecords } =
    React.useMemo(() => {
      const sliceStart = page * pageSize;
      const slickEnd = page * pageSize + pageSize;

      const sorted = stableSort(data, getComparator<T>(order, orderBy));
      const filtered = sorted.filter((row) => {
        // Can't do this because there could be other filter
        // components involved
        // if (!debouncedSearchTerm) return true;

        const isStringMatch = (value: any) => {
          return (
            (typeof value === "string" &&
              value
                .toLowerCase()
                .includes(debouncedSearchTerm.toLowerCase())) ||
            !debouncedSearchTerm
          );
        };

        const isNumberMatch = (value: any) => {
          return (
            (typeof value === "number" &&
              value
                .toString()
                .toLowerCase()
                .includes(debouncedSearchTerm.toLowerCase())) ||
            !debouncedSearchTerm
          );
        };

        const keys = Object.keys(row);

        const isMatch = Object.values(row)
          .filter((value: any, index: number) => {
            const key = keys[index];
            const columnExists = columns.some(
              (col) => col.propertyName === key,
            );
            return columnExists;
          })
          .some((value: any) => {
            return isStringMatch(value) || isNumberMatch(value);
          });

        return (
          isMatch && (!customFilterPredicate || customFilterPredicate(row))
        );
      });

      let paged = filtered.slice(sliceStart, slickEnd);
      let currentPage = page;

      // We could run into a situation where a custom filter has been applied
      // and we're stuck on a page with no data. In this case, just act like
      // we're on the first page. (trying to move to the first page without a
      // re-render)
      if (page > 0 && paged.length === 0 && data.length > 0) {
        currentPage = 0;
        paged = filtered.slice(0, pageSize);
      }

      return {
        filteredSortedAndPaged: paged,
        smartPage: currentPage,
        totalRecords: filtered.length,
      };
    }, [
      debouncedSearchTerm,
      order,
      orderBy,
      page,
      pageSize,
      columns,
      customFilterPredicate,
      data,
    ]);

  return (
    <MyGenericTable
      {...props}
      page={smartPage}
      setPage={setPage}
      order={order}
      setOrder={setOrder}
      pageSize={pageSize}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
      currentPageData={filteredSortedAndPaged}
      totalRecords={totalRecords}
      setPageSize={setPageSize}
      filterTerm={isFilterProps(props) ? props.filterTerm : filterTerm}
      setFilterTerm={isFilterProps(props) ? props.setFilterTerm : setFilterTerm}
    />
  );
};

export { MyLocalDataTable };
